import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { BulkPaymentResult, BulkPaymentMatch } from '../../../models/bulk-payment.model';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-bulk-payment-results-step',
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './results-step.component.html'
})
export class BulkPaymentResultsStepComponent {
  @Input() results: BulkPaymentResult[] = [];
  @Input() matches: BulkPaymentMatch[] = [];
  @Output() downloadResults = new EventEmitter<void>();
  @Output() viewReceipt = new EventEmitter<string>();
  @Output() downloadReceipt = new EventEmitter<string>();

  displayedColumns: string[] = ['status', 'accountNumber', 'customerName', 'amount', 'invoiceNumber', 'transactionId', 'message'];

  get successfulPayments(): BulkPaymentResult[] {
    return this.results.filter(r => r.status === 'success');
  }

  get failedPayments(): BulkPaymentResult[] {
    return this.results.filter(r => r.status === 'failed');
  }

  get skippedPayments(): BulkPaymentMatch[] {
    return this.matches.filter(m => m.status !== 'matched');
  }

  get totalAmount(): number {
    return this.successfulPayments.reduce((sum, result) => sum + result.row.amount, 0);
  }

  get hasCostOfCapital(): boolean {
    return this.results.some(r => (r.row.costOfCapitalAmount || 0) > 0);
  }

  get totalCostOfCapital(): number {
    return this.successfulPayments.reduce((sum, result) => sum + (result.row.costOfCapitalAmount || 0), 0);
  }

  get grandTotal(): number {
    return this.totalAmount + this.totalCostOfCapital;
  }

  get successRate(): number {
    const total = this.results.length;
    if (total === 0) return 0;
    return (this.successfulPayments.length / total) * 100;
  }

  getDisplayedColumns(): string[] {
    if (this.hasCostOfCapital) {
      return ['status', 'accountNumber', 'customerName', 'amount', 'costOfCapital', 'total', 'invoiceNumber', 'transactionId', 'message', 'actions'];
    }
    return ['status', 'accountNumber', 'customerName', 'amount', 'invoiceNumber', 'transactionId', 'message', 'actions'];
  }

  getOutcomeLabel(result: BulkPaymentResult): string {
    switch (result.status) {
      case 'success':
        return 'Approved';
      case 'failed':
        return 'Declined';
      default:
        return 'Pending';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'success':
        return 'check_circle';
      case 'failed':
        return 'error';
      default:
        return 'help';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'success':
        return 'status-success';
      case 'failed':
        return 'status-error';
      default:
        return 'status-default';
    }
  }

  onDownloadResults(): void {
    this.downloadResults.emit();
  }

  onViewReceipt(transactionId: string): void {
    this.viewReceipt.emit(transactionId);
  }

  onDownloadReceipt(transactionId: string): void {
    this.downloadReceipt.emit(transactionId);
  }
}

